export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "painting",
    title: "Painting",
    subtitle: "Interior & exterior painting done clean",
    price: "Free Estimate",
    image: "/assets/images/service-painting.jpg",
    description:
      "Painting is where F&T Renovations started, and it is still what we are known for. We prep properly — patch, caulk, sand, mask and prime — before a single finish coat goes on, because that prep is the difference between paint that looks great for a season and paint that looks great for years. Interior repaints, full-home colour changes, cabinets, trim, ceilings and exterior siding across Lawrenceville and metro Atlanta.",
    features: [
      "Interior repaints — walls, ceilings, trim & doors",
      "Exterior siding, soffit, fascia & shutters",
      "Cabinet and built-in refinishing",
      "Full patch, caulk, sand and prime prep",
      "Drop cloths, masking and daily clean-up",
      "Straight lines, even coverage, no drips",
    ],
    process: [
      { step: "Walkthrough", desc: "We measure the space, check surface condition and talk through colours and sheens." },
      { step: "Written Quote", desc: "You get one clear price covering prep, materials, coats and clean-up." },
      { step: "Prep & Protect", desc: "Furniture covered, floors protected, surfaces patched, sanded and primed." },
      { step: "Paint & Inspect", desc: "Finish coats go on, then we walk the job with you before we pack up." },
    ],
  },
  {
    slug: "bathroom-remodeling",
    title: "Bathroom Remodeling",
    subtitle: "Full bathroom renovations, start to finish",
    price: "Free Estimate",
    image: "/assets/images/service-bathrooms.jpg",
    description:
      "A bathroom touches every trade at once — demo, plumbing, electrical, waterproofing, tile, drywall and paint. Because F&T Renovations self-performs all of it, you deal with one crew and one schedule instead of chasing five subcontractors. Tub-to-shower conversions, vanity and fixture upgrades, full gut renovations and guest-bath refreshes throughout Gwinnett and North Fulton.",
    features: [
      "Full gut renovations & tub-to-shower conversions",
      "Vanity, toilet, tub and fixture replacement",
      "Waterproofing before any tile goes down",
      "Tile floors, shower walls and niches",
      "Plumbing and electrical handled in-house",
      "Drywall, trim and finish painting included",
    ],
    process: [
      { step: "Design & Scope", desc: "We review layout, fixtures and finishes, then confirm exactly what is included." },
      { step: "Demo & Rough-In", desc: "Old surfaces come out; plumbing and electrical are reworked to suit the new layout." },
      { step: "Waterproof & Tile", desc: "Wet areas are properly waterproofed before tile is set, floated and grouted." },
      { step: "Finish & Detail", desc: "Fixtures, trim, paint and a final punch list walkthrough with you." },
    ],
  },
  {
    slug: "flooring",
    title: "Flooring",
    subtitle: "Hardwood, laminate, LVP & more",
    price: "Free Estimate",
    image: "/assets/images/service-flooring.jpg",
    description:
      "New flooring changes a room faster than almost anything else. We install hardwood, engineered wood, laminate and luxury vinyl plank — and we spend the time on subfloor prep that keeps a floor flat, quiet and square years later. Includes tear-out and haul-away of the old floor, transitions, and new baseboard or shoe moulding to finish the edges properly.",
    features: [
      "Hardwood & engineered wood installation",
      "Laminate and luxury vinyl plank (LVP)",
      "Subfloor levelling, prep & moisture barrier",
      "Old flooring tear-out and haul-away",
      "Stair treads, transitions & thresholds",
      "New baseboard or shoe moulding to finish",
    ],
    process: [
      { step: "Measure & Select", desc: "We measure the space and help you compare materials against budget and traffic." },
      { step: "Tear-Out & Prep", desc: "Old flooring is removed and the subfloor is levelled, cleaned and prepped." },
      { step: "Installation", desc: "Planks are racked and laid for a natural pattern with proper expansion gaps." },
      { step: "Trim & Finish", desc: "Transitions and moulding go on, the site is cleaned, and we walk it with you." },
    ],
  },
  {
    slug: "plumbing",
    title: "Plumbing",
    subtitle: "Fixtures, repairs & remodel rough-in",
    price: "Free Estimate",
    image: "/assets/images/service-plumbing.jpg",
    description:
      "Plumbing work tied to the spaces we remodel — and standalone repairs when something needs fixing now. We handle fixture replacement, supply and drain lines, relocations for a new bathroom or kitchen layout, and the rough-in that has to be right before walls close up. Leaks get traced to the source rather than patched at the symptom.",
    features: [
      "Sink, faucet, toilet & tub/shower fixtures",
      "Supply and drain line repair or replacement",
      "Rough-in plumbing for remodels",
      "Fixture relocation for new layouts",
      "Leak tracing and water damage repair",
      "Shut-off valve and supply line upgrades",
    ],
    process: [
      { step: "Diagnose", desc: "We find the actual source of the problem before quoting a repair." },
      { step: "Clear Quote", desc: "You get the parts, labour and scope in writing before we start." },
      { step: "Repair or Rough-In", desc: "Work is done to code and pressure-checked before anything is closed up." },
      { step: "Restore", desc: "We patch, repair and repaint what we opened — the wall goes back the way it was." },
    ],
  },
  {
    slug: "electrical",
    title: "Electrical",
    subtitle: "Outlets, fixtures, switches & circuits",
    price: "Free Estimate",
    image: "/assets/images/service-electrical.jpg",
    description:
      "Residential electrical work for the rooms we build and remodel. Adding recessed lighting to a basement, running circuits for a renovated kitchen, replacing tired outlets and switches, mounting fixtures and ceiling fans, or upgrading to GFCI protection where code requires it. Everything is installed to code and tested before we call it finished.",
    features: [
      "Outlet, switch and GFCI installation",
      "Recessed lighting & fixture mounting",
      "Ceiling fan installation and replacement",
      "New circuits for remodels and additions",
      "Basement and addition rough-in wiring",
      "Troubleshooting dead outlets and circuits",
    ],
    process: [
      { step: "Assess", desc: "We check the existing panel and circuits to confirm what the job actually needs." },
      { step: "Plan & Quote", desc: "Fixture locations and circuit loads are mapped, then priced in writing." },
      { step: "Rough-In & Wire", desc: "Circuits are run and boxes set to code, ready for inspection where required." },
      { step: "Trim & Test", desc: "Devices and fixtures are installed, tested and labelled at the panel." },
    ],
  },
  {
    slug: "sheetrock",
    title: "Sheetrock & Drywall",
    subtitle: "Hanging, finishing & repair",
    price: "Free Estimate",
    image: "/assets/images/service-sheetrock.jpg",
    description:
      "Drywall is the surface everything else gets judged against — if the finish is wavy, the paint will show it. We hang, tape, mud and sand to a clean, flat finish, match existing wall and ceiling textures on repairs, and handle everything from a single patch to a full basement build-out. Water damage, popped screws, cracked corners and holes all get fixed properly, not just skimmed over.",
    features: [
      "Full-room and whole-house hanging",
      "Taping, mudding and sanding to a flat finish",
      "Patch repair for holes, cracks & water damage",
      "Ceiling repair and texture matching",
      "Popcorn ceiling removal and smooth finish",
      "Corner bead, archways and detail work",
    ],
    process: [
      { step: "Assess", desc: "We check framing, moisture and existing texture so the repair actually blends." },
      { step: "Hang or Cut In", desc: "Board is hung tight and square, or the damaged section is cut back cleanly." },
      { step: "Tape & Finish", desc: "Multiple coats of mud, sanded flat, with texture matched where needed." },
      { step: "Prime & Paint", desc: "Primer and paint go on so the repair disappears into the wall." },
    ],
  },
  {
    slug: "tile-work",
    title: "Tile Work",
    subtitle: "Floors, showers, backsplashes & more",
    price: "Free Estimate",
    image: "/assets/images/service-tile.jpg",
    description:
      "Tile is unforgiving — lippage, crooked grout lines and a badly cut edge are permanent. We set tile on a properly prepped, level substrate, use levelling systems on large-format tile, keep grout lines consistent, and waterproof every wet area before the first tile goes on. Porcelain, ceramic, stone and mosaic for floors, showers, tub surrounds and backsplashes.",
    features: [
      "Bathroom floors, showers & tub surrounds",
      "Kitchen and bar backsplashes",
      "Large-format and porcelain tile",
      "Natural stone and mosaic accents",
      "Waterproofing and substrate prep",
      "Shower niches, benches and curbs",
    ],
    process: [
      { step: "Layout", desc: "We dry-lay the pattern so cuts land where they look intentional, not awkward." },
      { step: "Prep & Waterproof", desc: "Substrate is levelled and wet areas are waterproofed before setting starts." },
      { step: "Set & Level", desc: "Tile is set in thinset with levelling clips for a flat, lippage-free surface." },
      { step: "Grout & Seal", desc: "Grout is floated and cleaned, joints are caulked, and stone is sealed." },
    ],
  },
  {
    slug: "decks",
    title: "Decks",
    subtitle: "New builds, rebuilds & deck repair",
    price: "Free Estimate",
    image: "/assets/images/service-decks.jpg",
    description:
      "Georgia summers and humidity are hard on outdoor structures. We build new decks, rebuild tired ones, and repair the framing, boards, railings and stairs that fail first. Pressure-treated and composite decking, proper flashing at the ledger, and code-compliant railings and footings — the parts nobody sees are the parts that decide whether a deck lasts.",
    features: [
      "New deck design and construction",
      "Deck rebuilds and board replacement",
      "Railing, stair and framing repair",
      "Pressure-treated and composite decking",
      "Proper ledger flashing and footings",
      "Sanding, staining and sealing",
    ],
    process: [
      { step: "Site Visit", desc: "We check span, grade, access and the condition of any existing structure." },
      { step: "Plan & Quote", desc: "Layout, materials and railing style are confirmed with a written price." },
      { step: "Frame & Build", desc: "Footings, ledger, framing and decking are built to code and to spec." },
      { step: "Finish & Seal", desc: "Railings and stairs go on, then the surface is sanded and sealed." },
    ],
  },
  {
    slug: "basements",
    title: "Basement Finishing",
    subtitle: "Turn unfinished space into real living space",
    price: "Free Estimate",
    image: "/assets/images/service-basements.jpg",
    description:
      "A finished basement is the cheapest square footage you will ever add to a metro Atlanta home. F&T Renovations takes a bare basement all the way through framing, electrical, plumbing, insulation, drywall, flooring, trim and paint — including full baths, wet bars, media rooms, home offices and in-law suites. One crew, one schedule, one point of contact from bare block to finished room.",
    features: [
      "Framing, insulation and drywall",
      "Electrical and recessed lighting throughout",
      "Full or half bathroom additions",
      "Wet bars, media rooms & home offices",
      "Flooring, trim, doors and paint",
      "Egress, stair and railing work",
    ],
    process: [
      { step: "Plan the Space", desc: "We lay out rooms, bath location and lighting around how you will actually use it." },
      { step: "Frame & Rough-In", desc: "Walls go up, then electrical and plumbing are run and inspected." },
      { step: "Close & Finish", desc: "Insulation, drywall, flooring and trim turn the shell into finished rooms." },
      { step: "Final Walkthrough", desc: "Paint, fixtures and doors are completed, then we walk the punch list with you." },
    ],
  },
];
